import { useMemo, useState } from "react";
import type { RoomView, TradeSide } from "@grundbesitz/shared";
import {
  counterError,
  emptyTradeSide,
  isTradeSideEmpty,
  mortgagedNotes,
  proposeError,
  tradeSummary,
} from "../../../lib/assets";
import { useStore } from "../../../lib/store";
import { Button } from "../../ui/Button";
import { PlayerChip } from "../assets/PlayerChip";
import { useAct } from "../assets/useAct";
import { SidePicker } from "./SidePicker";

export interface EditorSeed {
  /** Bumps when a new editor is opened, so the form resets. */
  key: number;
  to: string | null;
  give: TradeSide;
  get: TradeSide;
  /** Set for a counter offer: the incoming offer that is answered. */
  counterOf: number | null;
}

/** Editor for a new offer or a counter offer. Validation is the engine's `PROPOSE_TRADE` / `COUNTER_TRADE` check. */
export function TradeEditor({ room, seed, onClose }: { room: RoomView; seed: EditorSeed; onClose: () => void }) {
  const game = room.game!;
  const meId = room.you.playerId!;
  const me = game.players.find((p) => p.id === meId)!;
  const toast = useStore((s) => s.toast);
  const { busy, run } = useAct();

  const partners = game.players.filter((p) => !p.eliminated && p.id !== meId);
  const [to, setTo] = useState<string | null>(seed.to ?? (partners.length === 1 ? partners[0].id : null));
  const [give, setGive] = useState<TradeSide>(seed.give);
  const [get, setGet] = useState<TradeSide>(seed.get);
  const partner = partners.find((p) => p.id === to) ?? null;
  const counter = seed.counterOf !== null;

  const error = useMemo(
    () =>
      seed.counterOf !== null
        ? counterError(game, meId, seed.counterOf, give, get)
        : proposeError(game, meId, to, give, get),
    [game, meId, seed.counterOf, to, give, get],
  );

  const bothEmpty = isTradeSideEmpty(give) && isTradeSideEmpty(get);
  const notes = [...mortgagedNotes(game, give), ...mortgagedNotes(game, get)];

  const pick = (id: string) => {
    if (id === to) return;
    setTo(id);
    setGet(emptyTradeSide());
  };

  const submit = async () => {
    if (error || !partner) return;
    const ack = counter
      ? await run("counter", { type: "COUNTER_TRADE", tradeId: seed.counterOf!, give, get })
      : await run("propose", { type: "PROPOSE_TRADE", to: partner.id, give, get });
    if (ack.ok) {
      toast(counter ? `Gegenangebot an ${partner.name} gesendet.` : `Angebot an ${partner.name} gesendet.`, "success");
      onClose();
    }
  };

  return (
    <section aria-label={counter ? "Gegenangebot" : "Neues Angebot"} className="space-y-3 rounded-2xl border-2 border-accent bg-surface p-3.5">
      <div className="flex items-center justify-between gap-2">
        <h3 className="font-display text-lg font-semibold">{counter ? "Gegenangebot" : "Neues Angebot"}</h3>
        <Button variant="ghost" onClick={onClose} className="-mr-2">
          Verwerfen
        </Button>
      </div>

      {counter && partner ? (
        <p className="text-sm">
          Antwort an <PlayerChip game={game} members={room.members} id={partner.id} />. Die Seiten sind aus deiner Sicht gespiegelt.
        </p>
      ) : partners.length === 0 ? (
        <p className="text-sm text-ink-2">Es ist kein Mitspieler mehr im Spiel, mit dem du handeln könntest.</p>
      ) : (
        <fieldset>
          <legend className="text-sm font-semibold">Handelspartner</legend>
          <div className="mt-1.5 flex flex-wrap gap-1.5">
            {partners.map((p) => (
              <button
                key={p.id}
                type="button"
                aria-pressed={p.id === to}
                onClick={() => pick(p.id)}
                className={`inline-flex min-h-11 items-center rounded-lg border-2 px-3 text-sm transition-colors ${
                  p.id === to ? "border-accent bg-accent-soft" : "border-line bg-bg hover:bg-surface-2"
                }`}
              >
                <PlayerChip game={game} members={room.members} id={p.id} />
              </button>
            ))}
          </div>
        </fieldset>
      )}

      {partner ? (
        <div className="grid gap-3">
          <SidePicker game={game} owner={me} title="Du gibst" side={give} onChange={setGive} />
          <SidePicker game={game} owner={partner} title={`Du bekommst von ${partner.name}`} side={get} onChange={setGet} />
        </div>
      ) : (
        partners.length > 0 && <p className="text-sm text-ink-2">Wähle einen Mitspieler, um Grundstücke, Bargeld und Freikarten zu sehen.</p>
      )}

      {partner && (
        <div className="space-y-2 rounded-xl bg-surface-2 p-3 text-sm">
          <p aria-live="polite">
            <strong>{bothEmpty ? "Noch nichts ausgewählt." : tradeSummary(give, get)}</strong>
          </p>
          {notes.map((n) => (
            <p key={n} className="text-xs">
              <span className="font-semibold text-warn">Belastetes Grundstück.</span> {n}
            </p>
          ))}
          {error && !bothEmpty && (
            <p role="alert" className="text-xs font-semibold text-danger">
              {error}
            </p>
          )}
          <p className="text-xs text-ink-2">
            Ein Angebot besteht nur aus Bargeld, Grundstücken und Freikarten. Kredite und Absprachen über künftige Miete gibt es nicht.
            Grundstücke mit Gebäuden in der Farbgruppe müssen erst an die Bank verkauft werden.
          </p>
        </div>
      )}

      <Button variant="primary" className="w-full" disabled={!partner || error !== null || busy !== null} onClick={() => void submit()}>
        {counter ? "Gegenangebot senden" : "Angebot senden"}
      </Button>
    </section>
  );
}
